import { GroceryStoreKey } from "./grocery.types";

/**
 * Per-store allowlist of the supermarket hosts a live-price refresh may fetch.
 *
 * The refresh fetches whatever URL the catalog stored for a store, straight
 * from the user's own device/browser — over Capacitor's native HTTP on mobile
 * (device IP, no CORS) or the extension on web. A poisoned, stale, or otherwise
 * bad catalog row must not turn that into an open fetch to an arbitrary host
 * (internal IPs on device, any origin via the extension). So every source URL
 * is pinned to its own supermarket's https host before it is ever fetched.
 *
 * Exact host or any subdomain of it is accepted (e.g. `www.` / `api.`); the
 * scheme must be https.
 */
const ALLOWED_STORE_HOSTS: Record<GroceryStoreKey, readonly string[]> = {
  woolworths: ["woolworths.com.au"],
  coles: ["coles.com.au"],
  aldi: ["aldi.com.au"],
};

/**
 * True only when `rawUrl` is an https URL whose host is (or is a subdomain of)
 * one of `store`'s allowed supermarket domains. Anything unparseable,
 * non-https, or off-host is rejected — including credential-in-host tricks like
 * `https://woolworths.com.au@evil.com/`, which parses to host `evil.com`.
 */
export function isAllowedStoreUrl(store: GroceryStoreKey, rawUrl: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") {
    return false;
  }
  const host = parsed.hostname.toLowerCase();
  // Look the key up as an own property only. A store key coming from an
  // unvalidated catalog row can be an inherited name like `constructor` or
  // `toString`, which would resolve to an `Object.prototype` member (a
  // function) rather than `undefined` — `?? []` would not fire and `.some`
  // would throw. `hasOwnProperty` keeps this failing closed to `[]`.
  const allowed = Object.prototype.hasOwnProperty.call(ALLOWED_STORE_HOSTS, store)
    ? ALLOWED_STORE_HOSTS[store]
    : [];
  return allowed.some((domain) => host === domain || host.endsWith(`.${domain}`));
}
